import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  loginUser,
  logoutUser,
  getCurrentUser,
  isAuthenticated,
} from "../services/authService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // =========================================================
  // RESTORE LOGIN
  // =========================================================

  useEffect(() => {
    const restoreLogin = async () => {
      try {
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token");

        // No token
        if (!token) {
          setUser(null);
          return;
        }

        // Validate token/session with backend
        const result = await getCurrentUser();

        if (result?.success) {
          setUser(result.user);
        } else {
          // Invalid/expired session
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");

          setUser(null);
        }
      } catch (error) {
        console.error("Restore login error:", error);

        localStorage.removeItem("token");
        sessionStorage.removeItem("token");

        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    restoreLogin();
  }, []);

  // =========================================================
  // LOGIN
  // =========================================================

  const login = async (
    username,
    password,
    rememberMe = true
  ) => {
    try {
      setLoading(true);

      const result = await loginUser(
        username,
        password,
        rememberMe
      );

      if (result?.success) {
        setUser(result.user);

        return {
          success: true,
          user: result.user,
          message: result.message,
        };
      }

      return {
        success: false,
        message:
          result?.message ||
          "Invalid username or password",
      };
    } catch (error) {
      console.error("Login error:", error);

      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Unable to connect to server",
      };
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // LOGOUT
  // =========================================================

  const logout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");

      setUser(null);
    }
  };

  // =========================================================
  // AUTHENTICATED STATUS
  // =========================================================

  const authenticated =
    !!user && isAuthenticated();

  // =========================================================
  // PROVIDER
  // =========================================================

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: authenticated,
        login,
        logout,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// =========================================================
// HOOK
// =========================================================

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth must be used inside AuthProvider"
    );
  }

  return context;
};